import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import useInView from '../../utils/useInView';

interface AnimatedSectionProps {
  children: React.ReactNode;
  delay?: number;
  distance?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  threshold?: number;
  className?: string;
  id?: string;
}

const SectionContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  delay = 0,
  distance = 50,
  duration = 0.6,
  direction = 'up',
  threshold = 0.1,
  className,
  id
}) => {
  const [ref, inView] = useInView({
    threshold,
    triggerOnce: true,
    rootMargin: '0px 0px -100px 0px' // Attiva l'animazione leggermente prima che l'elemento sia completamente nel viewport
  });
  
  const getInitialPosition = () => {
    switch (direction) {
      case 'up': return { opacity: 0, y: distance };
      case 'down': return { opacity: 0, y: -distance };
      case 'left': return { opacity: 0, x: distance };
      case 'right': return { opacity: 0, x: -distance };
      case 'none': return { opacity: 0 };
      default: return { opacity: 0, y: distance };
    }
  };
  
  const getFinalPosition = () => {
    return { 
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1.0] // Cubic bezier per un'animazione più naturale
      }
    };
  };
  
  return (
    <SectionContainer 
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      id={id}
    >
      <motion.div
        initial={getInitialPosition()}
        animate={inView ? getFinalPosition() : getInitialPosition()}
      >
        {children}
      </motion.div>
    </SectionContainer>
  );
};

export default AnimatedSection; 